import React, { useState } from 'react';
import api from '../services/api';

const AITestCaseGenerator = () => {
  const [questionTitle, setQuestionTitle] = useState('');
  const [questionDescription, setQuestionDescription] = useState('');
  const [difficulty, setDifficulty] = useState('MEDIUM');
  const [testCaseCount, setTestCaseCount] = useState(5);
  const [generatedTestCases, setGeneratedTestCases] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const generateTestCases = async () => {
    if (!questionTitle.trim() || !questionDescription.trim()) {
      setError('⚠️ Please provide both question title and description');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedTestCases('');

    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/ai-assistant/generate-test-cases',
        {
          questionTitle: questionTitle,
          questionDescription: questionDescription,
          difficulty: difficulty,
          count: testCaseCount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGeneratedTestCases(response.data.testCases);
    } catch (err) {
      console.error('Failed to generate test cases:', err);
      setError(err.response?.data?.error || 'Failed to generate test cases. Make sure OpenAI is configured.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedTestCases);
    setError('✅ Test cases copied to clipboard!');
    setTimeout(() => setError(''), 2000);
  };

  const clearForm = () => {
    setQuestionTitle('');
    setQuestionDescription('');
    setDifficulty('MEDIUM');
    setTestCaseCount(5);
    setGeneratedTestCases('');
    setError('');
  };

  return (
    <div className="ai-test-case-generator" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '30px', 
        borderRadius: '15px', 
        marginBottom: '30px',
        boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '30px'
          }}>
            ⚙️
          </div>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '28px', fontWeight: 'bold' }}>
              AI Test Case Generator
            </h2>
            <p style={{ margin: '5px 0 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
              Generate comprehensive test cases including edge cases using AI
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success Message */}
      {error && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '10px',
          background: error.startsWith('✅') ? '#d4edda' : '#f8d7da',
          color: error.startsWith('✅') ? '#155724' : '#721c24',
          border: `1px solid ${error.startsWith('✅') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {error}
        </div>
      )}

      {/* Input Section */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '30px',
        marginBottom: '30px'
      }}>
        {/* Left Column - Input */}
        <div style={{ 
          background: 'white', 
          padding: '25px', 
          borderRadius: '12px', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ marginTop: 0, color: '#333', fontSize: '20px', marginBottom: '20px' }}>
            📝 Problem Details
          </h3>

          {/* Question Title */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#555',
              fontSize: '14px'
            }}>
              Question Title *
            </label>
            <input
              type="text"
              value={questionTitle}
              onChange={(e) => setQuestionTitle(e.target.value)}
              placeholder="e.g., Two Sum"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Question Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#555',
              fontSize: '14px'
            }}>
              Question Description *
            </label>
            <textarea
              value={questionDescription}
              onChange={(e) => setQuestionDescription(e.target.value)}
              placeholder="Describe the problem in detail. Include constraints, input/output format, and examples..."
              rows={10}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Difficulty & Count */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}>
                Difficulty Level
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="EASY">Easy</option>
                <option value="MEDIUM">Medium</option>
                <option value="HARD">Hard</option>
                <option value="EXPERT">Expert</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}>
                Number of Test Cases
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={testCaseCount}
                onChange={(e) => setTestCaseCount(parseInt(e.target.value) || 5)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={generateTestCases}
              disabled={isGenerating}
              style={{
                flex: 1,
                padding: '14px',
                background: isGenerating ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              {isGenerating ? '⏳ Generating...' : '⚙️ Generate Test Cases'}
            </button>

            <button
              onClick={clearForm}
              disabled={isGenerating}
              style={{
                padding: '14px 20px',
                background: '#f5f5f5',
                color: '#666',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.target.style.background = '#ececec';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f5f5f5';
              }}
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        {/* Right Column - Output */}
        <div style={{ 
          background: 'white', 
          padding: '25px', 
          borderRadius: '12px', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '20px' }}>
              🎯 Generated Test Cases
            </h3>
            {generatedTestCases && (
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '8px 16px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#5568d3'}
                onMouseLeave={(e) => e.target.style.background = '#667eea'}
              >
                📋 Copy
              </button>
            )}
          </div>

          {!generatedTestCases && !isGenerating && (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#999',
              background: '#f9f9f9',
              borderRadius: '8px',
              border: '2px dashed #e0e0e0',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '60px', marginBottom: '15px' }}>⚙️</div>
              <p style={{ fontSize: '16px', margin: 0 }}>
                Fill in the problem details and click "Generate Test Cases"
              </p>
              <p style={{ fontSize: '14px', margin: '10px 0 0 0', color: '#bbb' }}>
                AI will create comprehensive test cases including edge cases
              </p>
            </div>
          )}

          {isGenerating && (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              background: '#f9f9f9',
              borderRadius: '8px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '60px', marginBottom: '15px', animation: 'spin 2s linear infinite' }}>⚙️</div>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#667eea', margin: 0 }}>
                Generating Test Cases...
              </p>
              <p style={{ fontSize: '14px', margin: '10px 0 0 0', color: '#999' }}>
                AI is analyzing your problem and creating comprehensive test cases
              </p>
            </div>
          )}

          {generatedTestCases && !isGenerating && (
            <div style={{
              background: '#ffffff',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              maxHeight: '500px',
              overflowY: 'auto'
            }}>
              <pre style={{
                fontFamily: 'monospace',
                fontSize: '13px',
                lineHeight: '1.6',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                color: '#000000',
                background: '#ffffff'
              }}>
                {generatedTestCases}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        padding: '20px',
        borderRadius: '10px',
        color: 'white'
      }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
          💡 Tips for Best Results:
        </h4>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>Provide a clear, detailed problem description with constraints</li>
          <li>Include example inputs and outputs in your description</li>
          <li>Specify input/output formats clearly (arrays, strings, numbers, etc.)</li>
          <li>AI will automatically generate edge cases, large inputs, and corner cases</li>
          <li>Review and adjust generated test cases as needed before using them</li>
        </ul>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AITestCaseGenerator;


